import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { TextFieldClasses } from '@mui/material/TextField/textFieldClasses';
import { useTheme } from '@mui/material/styles';

import clsx from 'clsx';
import { getIn } from 'formik';
import { FormikValues } from 'formik/dist/types';

import { useUserConfig } from '../../../context/UserConfigContext';
import { ExerciseOptionType } from '../../../shared/constants';
import { getTimeValueFromOptionValue } from '../../../utils/datetime/date-time-utils';
import { getFormikValue } from '../../../utils/forms/formikFunctions';
import AppFormStandardTextField from '../forms/app-form-standard-text-field';
import AppTimeSetterField from '../wheels/app-time-setter-field/AppTimeSetterField';
import { useStyles } from './AppFormOptionTextField.styles';

type CustomTextFieldClasses = Partial<TextFieldClasses> & {
  number?: string;
  time?: string;
};

interface AppFormOptionTextFieldProps {
  type: ExerciseOptionType;
  name: string;
  formik: FormikValues;
  placeholder?: string | number;
  endAdornment?: string;
  classes?: CustomTextFieldClasses;
  onPlayClick?: () => void;
  onChange?: (e: number) => void;
  playing?: boolean;
  fullWidth?: boolean;
  viewMode?: boolean;
}

export default function AppFormOptionTextField(
  props: AppFormOptionTextFieldProps,
) {
  const {
    type,
    name,
    formik,
    classes: customClasses = {},
    endAdornment,
    placeholder,
    onPlayClick,
    playing,
    onChange,
    fullWidth,
    viewMode,
  } = props;
  const { number: numberClass, time: timeClass, ...classes } = customClasses;
  const { t } = useTranslation();
  const theme = useTheme();
  const { options } = useUserConfig();

  const { classes: optionClasses } = useStyles(theme);
  const showExerciseUnits = options.showExerciseUnits;

  const spanRef = useRef(null);
  const [textWidth, setTextWidth] = useState(0);

  useEffect(() => {
    if (spanRef.current) {
      setTextWidth(spanRef.current.offsetWidth);
    }
  }, [getIn(formik.values, name), viewMode]);

  const isNumber = !Number.isNaN(+('' + getIn(formik.values, name)));
  const optionValue = getIn(formik.values, name);
  const hasOptionValue =
    optionValue !== null &&
    optionValue !== undefined &&
    (optionValue === 0 || !!optionValue);

  switch (type) {
    case ExerciseOptionType.integer:
    case ExerciseOptionType.fractional: {
      return (
        <Box className={optionClasses.fieldContainer}>
          <Box ref={spanRef} className={optionClasses.fakeInput}>
            {isNumber ? getIn(formik.values, name) : placeholder}
          </Box>
          {viewMode && (
            <Box className={optionClasses.viewModeField}>
              {hasOptionValue ? (
                <>
                  {' '}
                  <Typography variant="text-md">{optionValue}</Typography>
                  {showExerciseUnits && (
                    <Typography fontSize="10px" marginBottom="-4px">
                      {endAdornment}
                    </Typography>
                  )}
                </>
              ) : (
                '-'
              )}
            </Box>
          )}
          {!viewMode && (
            <AppFormStandardTextField
              placeholder={
                Number.isInteger(placeholder)
                  ? `${placeholder?.toString()}`
                  : placeholder?.toString()
              }
              type="number"
              inputMode="decimal"
              inputProps={{
                pattern: '^\\d*(\\.\\d+)?$',
              }}
              selectOnClick={true}
              InputProps={{
                endAdornment:
                  textWidth > 0 && showExerciseUnits ? (
                    <Box
                      className={clsx(
                        optionClasses.unitView,
                        !isNumber && optionClasses.programValue,
                      )}
                      left={`${textWidth}px`}>
                      {endAdornment}
                    </Box>
                  ) : null,
              }}
              onChange={onChange}
              formik={formik}
              name={name}
              className={numberClass}
              classes={classes}
            />
          )}
        </Box>
      );
    }
    case ExerciseOptionType.time: {
      const rawValue = getIn(formik?.values, name);
      const value = rawValue === undefined ? null : rawValue;
      const valueString = Number.isInteger(value)
        ? getTimeValueFromOptionValue(value as number, t)
        : value
          ? String(value)
          : '';

      if (viewMode)
        return (
          <Box className={optionClasses.viewModeField}>
            {valueString ? `${valueString}${endAdornment}` : '-'}
          </Box>
        );

      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          {/*{onPlayClick && (*/}
          {/*  <PlayActionButton*/}
          {/*    playing={playing}*/}
          {/*    disabled={playing}*/}
          {/*    onClick={onPlayClick}*/}
          {/*  />*/}
          {/*)}*/}

          <AppTimeSetterField
            value={value}
            placeholder={placeholder || null}
            fullWidth={fullWidth}
            onChange={(value) =>
              onChange ? onChange(value) : formik.setFieldValue(name, value)
            }
            classes={classes}
          />
        </Box>
      );
    }
    default:
      return null;
  }
}
